import { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';

interface CelebrationOverlayProps {
  isActive: boolean;
  duration?: number; // Duration in milliseconds
  onComplete?: () => void;
}

// Celebration background colors - soft, vibrant pastels
const CELEBRATION_COLORS = [
  'rgba(186, 255, 201, 0.3)',  // Mint green
  'rgba(186, 225, 255, 0.3)',  // Light blue
  'rgba(221, 186, 255, 0.3)',  // Lavender
  'rgba(255, 218, 185, 0.3)',  // Peach
  'rgba(255, 255, 186, 0.3)',  // Light yellow
  'rgba(255, 182, 193, 0.3)',  // Light pink
];

/**
 * Full-screen confetti celebration overlay with color-cycling background.
 * Triggers a colorful confetti burst animation when isActive becomes true.
 */
export function CelebrationOverlay({
  isActive,
  duration = 3000,
  onComplete,
}: CelebrationOverlayProps) {
  const hasTriggeredRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentColorIndex, setCurrentColorIndex] = useState(0);

  // Handle background color cycling
  useEffect(() => {
    if (!isActive) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
    setCurrentColorIndex(0);

    // Cycle through colors every 500ms (6 colors over 3 seconds)
    const colorInterval = setInterval(() => {
      setCurrentColorIndex((prev) => (prev + 1) % CELEBRATION_COLORS.length);
    }, 500);

    // Clean up after duration
    const hideTimeout = setTimeout(() => {
      setIsVisible(false);
    }, duration);

    return () => {
      clearInterval(colorInterval);
      clearTimeout(hideTimeout);
    };
  }, [isActive, duration]);

  // Handle confetti animation
  useEffect(() => {
    if (!isActive || hasTriggeredRef.current) return;

    hasTriggeredRef.current = true;
    const startTime = Date.now();
    const endTime = startTime + duration;

    // Create confetti colors - celebratory palette
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dfe6e9', '#a29bfe'];

    // Function to fire confetti from both sides
    const fireConfetti = () => {
      const timeLeft = endTime - Date.now();
      
      if (timeLeft <= 0) {
        // Animation complete
        if (onComplete) {
          onComplete();
        }
        return;
      }

      // Calculate intensity based on time remaining (more intense at start)
      const progress = 1 - timeLeft / duration;
      const particleCount = Math.max(1, Math.floor(8 * (1 - progress * 0.5)));

      // Fire from left side
      confetti({
        particleCount,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors,
        ticks: 200,
        gravity: 1.2,
        scalar: 1.2,
        drift: 0,
      });

      // Fire from right side
      confetti({
        particleCount,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors,
        ticks: 200,
        gravity: 1.2,
        scalar: 1.2,
        drift: 0,
      });

      // Also fire some from the center top for variety
      if (Math.random() > 0.5) {
        confetti({
          particleCount: Math.floor(particleCount * 1.5),
          angle: 90,
          spread: 120,
          origin: { x: 0.5, y: 0 },
          colors,
          ticks: 250,
          gravity: 1,
          scalar: 1,
        });
      }

      // Schedule next frame
      animationFrameRef.current = requestAnimationFrame(() => {
        setTimeout(fireConfetti, 100); // Fire every 100ms
      });
    };

    // Start the confetti
    fireConfetti();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, duration, onComplete]);

  // Reset the trigger when isActive becomes false
  useEffect(() => {
    if (!isActive) {
      hasTriggeredRef.current = false;
    }
  }, [isActive]);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-50 transition-all duration-500 ease-in-out"
      style={{
        backgroundColor: CELEBRATION_COLORS[currentColorIndex],
      }}
      aria-hidden="true"
    />
  );
}
